/**
 * M.L. Jewellers & Kitchenware - Luxury Image Fallback & Resolution Helper
 * Ensures no product ever renders as a pitch-black box or broken icon.
 */

export const FALLBACK_JEWELLERY_IMAGES = {
  ring: 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?auto=format&fit=crop&w=800&q=80',
  foliage_ring: 'https://images.unsplash.com/photo-1603561591411-07134e71a2a9?auto=format&fit=crop&w=800&q=80',
  jhumka: 'https://images.unsplash.com/photo-1630019852942-f89202989a59?auto=format&fit=crop&w=800&q=80',
  earrings: 'https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?auto=format&fit=crop&w=800&q=80',
  tops: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=800&q=80',
  necklace: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?auto=format&fit=crop&w=800&q=80',
  bridal: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=800&q=80',
  bangle: 'https://images.unsplash.com/photo-1611591475822-26c710ffc403?auto=format&fit=crop&w=800&q=80',
  kitchenware: 'https://images.unsplash.com/photo-1615865417491-9941019fbc00?auto=format&fit=crop&w=800&q=80',
  silverware: 'https://images.unsplash.com/photo-1611591475822-26c710ffc403?auto=format&fit=crop&w=800&q=80',
  gold_default: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=800&q=80'
};

/**
 * Returns a gorgeous, category-relevant luxury fallback image URL
 */
export function getJewelleryFallbackImage(product = {}) {
  const name = String(product?.name || '').toLowerCase();
  const category = String(product?.category || '').toLowerCase();
  const metal = String(product?.metal || product?.metalType || '').toLowerCase();

  if (category === 'kitchenware' || name.includes('cookware') || name.includes('handi') || name.includes('pot')) {
    return FALLBACK_JEWELLERY_IMAGES.kitchenware;
  }
  if (name.includes('foliage') || name.includes('floral ring')) {
    return FALLBACK_JEWELLERY_IMAGES.foliage_ring;
  }
  if (name.includes('jhumka') || category.includes('jhumka')) {
    return FALLBACK_JEWELLERY_IMAGES.jhumka;
  }
  if (name.includes('ring') || category.includes('ring')) {
    return FALLBACK_JEWELLERY_IMAGES.ring;
  }
  if (name.includes('tops') || name.includes('stud') || category.includes('earring') || name.includes('earring')) {
    return FALLBACK_JEWELLERY_IMAGES.tops;
  }
  if (category.includes('necklace') || name.includes('necklace') || name.includes('choker') || name.includes('haar')) {
    return FALLBACK_JEWELLERY_IMAGES.necklace;
  }
  if (category.includes('bangle') || name.includes('bangle') || name.includes('kada')) {
    return FALLBACK_JEWELLERY_IMAGES.bangle;
  }
  if (category.includes('bridal') || name.includes('bridal')) {
    return FALLBACK_JEWELLERY_IMAGES.bridal;
  }
  if (metal.includes('silver') || category.includes('silver')) {
    return FALLBACK_JEWELLERY_IMAGES.silverware;
  }
  return FALLBACK_JEWELLERY_IMAGES.gold_default;
}

export const isCapacitorNative = typeof window !== 'undefined' && Boolean(
  window.Capacitor?.isNativePlatform?.() ||
  (window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform() !== 'web') ||
  window.location.protocol === 'capacitor:' ||
  (window.location.hostname === 'localhost' && !window.location.port && (typeof navigator !== 'undefined' && navigator.userAgent.includes('Android')))
);

const LIVE_DOMAIN = 'https://mljewellersandkitchenware.com';

/**
 * Resolves a primary product image with robust fallback
 */
export function resolveProductImage(rawUrl, product = {}) {
  if (!rawUrl || typeof rawUrl !== 'string' || rawUrl.trim() === '') {
    return getJewelleryFallbackImage(product);
  }

  const trimmed = rawUrl.trim();

  // If already absolute HTTP/HTTPS or data URL, return as-is
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:image')) {
    return trimmed;
  }

  // Handle local relative paths (e.g. "api/uploads/...", "/api/uploads/...", "uploads/...")
  const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;

  // Inside native Android app, relative URLs must point to the live server
  if (isCapacitorNative) {
    return `${LIVE_DOMAIN}${cleanPath}`;
  }

  return cleanPath;
}

/**
 * Universal error handler for product <img> elements
 */
export function handleProductImageError(e, product = {}) {
  const fallback = getJewelleryFallbackImage(product);
  if (e?.target && e.target.src !== fallback) {
    e.target.onerror = null; // Prevent infinite error loops
    e.target.src = fallback;
  }
}
